
import { useState } from "react";
import { EmailRecord } from "@/types/email";
import { Investor, Contact } from "@/types/investor";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Search, Mail, Pencil, Trash2, Send } from "lucide-react";
import { getRelevanceBadgeVariant } from "@/utils/uiHelpers";

interface DraftsListProps {
  drafts: EmailRecord[];
  investors: Investor[];
  contacts: Contact[];
  onViewDraft: (draft: EmailRecord) => void;
  onDeleteDraft: (draft: EmailRecord) => void;
  onMarkAsSent: (draft: EmailRecord) => void;
}

const DraftsList = ({ 
  drafts, 
  investors, 
  contacts, 
  onViewDraft, 
  onDeleteDraft, 
  onMarkAsSent 
}: DraftsListProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  
  const getInvestor = (investorId: string): Investor | undefined => {
    return investors.find(investor => investor.id === investorId);
  };
  
  const getContact = (contactId: string): Contact | undefined => {
    return contacts.find(contact => contact.id === contactId);
  };
  
  // Check if an investor has been processed
  const isInvestorProcessed = (investor: Investor | undefined): boolean => {
    return !!investor?.lastRelevanceUpdate;
  };
  
  const filteredDrafts = drafts.filter(draft => {
    const investor = getInvestor(draft.investorId);
    const contact = getContact(draft.contactId);
    
    return (
      investor?.firm.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investor?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      draft.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });
  
  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Search by firm, investor, contact, or label..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Firm</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDrafts.length > 0 ? (
              filteredDrafts.map((draft) => {
                const investor = getInvestor(draft.investorId);
                const contact = getContact(draft.contactId);
                const processed = isInvestorProcessed(investor);
                const createdAt = draft.createdAt ? new Date(draft.createdAt) : new Date();
                
                return (
                  <TableRow key={draft.id}>
                    <TableCell>{draft.label || "Draft"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {investor?.firm || "Unknown"}
                        {investor && processed ? (
                          <Badge variant={getRelevanceBadgeVariant(investor.relevanceScore)}>
                            {investor.relevanceScore.toFixed(1)}
                          </Badge>
                        ) : investor ? (
                          <Badge variant="outline" className="bg-gray-100 text-gray-500">
                            0.0
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{contact?.name || "Unknown"}</TableCell>
                    <TableCell>
                      {format(createdAt, "MMM d, yyyy h:mm a")}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => onViewDraft(draft)}
                          className="flex items-center gap-1"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => onMarkAsSent(draft)}
                          className="flex items-center gap-1 bg-green-50 text-green-600 hover:bg-green-100 border-green-200"
                        >
                          <Send className="h-3 w-3" />
                          Mark as Sent
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => onDeleteDraft(draft)}
                          className="flex items-center gap-1 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  {searchTerm ? (
                    <div>No drafts found matching your search.</div>
                  ) : (
                    <div>
                      <p className="text-muted-foreground">No drafts found.</p>
                      <p className="text-sm mt-2">
                        Save an email as draft in the Email Generator to see it here.
                      </p>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DraftsList;
